import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { ScrollToTopOnMount } from "frame/components";
import { canPinJobs } from "utils";
import SitePinnedItems from "./site-pinned-items";
import SiteInfoCard from "./site-info-card";
import SiteTimesheetsPanel from "./site-timesheets-panel";
import SitePhotosNotesPanel from "./site-photos-notes-panel";
import SiteRecordSectionPanel from "./site-record-section-panel";
import {
	HazardSiteList,
	HazardousSubstancesSiteList,
	IncidentSiteList,
	InductionMinutesSiteList,
	SafetyMinutesSiteList,
	SafetyPlanChecklistSiteList,
} from "features/health-and-safety";
import SiteInspectionChecklistSiteList from "features/health-safety-forms/site-inspection-checklist/components/site-inspection-checklist-site-list-container";

const useStyles = makeStyles((theme) => ({
	siteInfoSlot: {
		margin: theme.spacing(2, 2, 0, 2),
		[theme.breakpoints.down("xs")]: {
			margin: theme.spacing(1, 1.5, 0, 1.5),
		},
	},
}));

/** At most one of these site summary accordions is expanded at a time. */
const SITE_SECTION_SITE_INFO = "siteInfo";
const SITE_SECTION_TIMESHEETS = "timesheets";
const SITE_SECTION_PHOTOS_NOTES = "photosNotes";
const SITE_SECTION_INCIDENTS = "incidents";
const SITE_SECTION_INDUCTIONS = "inductions";
const SITE_SECTION_SAFETY_MINUTES = "safetyMinutes";
const SITE_SECTION_SSPS = "ssps";
const SITE_SECTION_SITE_INSPECTIONS = "siteInspections";
const SITE_SECTION_HAZARDS = "hazards";
const SITE_SECTION_HAZARDOUS = "hazardousSubstances";

const Summary = ({ site, organisationId, role, onEdit, onDelete }) => {
	const classes = useStyles();
	const allowUnpin = canPinJobs(role);
	const [expandedSiteSection, setExpandedSiteSection] = useState(
		SITE_SECTION_SITE_INFO
	);

	const onSiteInfoExpanded = useCallback((event, isExpanded) => {
		setExpandedSiteSection(isExpanded ? SITE_SECTION_SITE_INFO : null);
	}, []);

	const onTimesheetsExpanded = useCallback((event, isExpanded) => {
		setExpandedSiteSection(isExpanded ? SITE_SECTION_TIMESHEETS : null);
	}, []);

	const onPhotosNotesExpanded = useCallback((event, isExpanded) => {
		setExpandedSiteSection(isExpanded ? SITE_SECTION_PHOTOS_NOTES : null);
	}, []);

	const onIncidentsExpanded = useCallback((event, isExpanded) => {
		setExpandedSiteSection(isExpanded ? SITE_SECTION_INCIDENTS : null);
	}, []);

	const onInductionsExpanded = useCallback((event, isExpanded) => {
		setExpandedSiteSection(isExpanded ? SITE_SECTION_INDUCTIONS : null);
	}, []);

	const onSafetyMinutesExpanded = useCallback((event, isExpanded) => {
		setExpandedSiteSection(isExpanded ? SITE_SECTION_SAFETY_MINUTES : null);
	}, []);

	const onSspsExpanded = useCallback((event, isExpanded) => {
		setExpandedSiteSection(isExpanded ? SITE_SECTION_SSPS : null);
	}, []);

	const onSiteInspectionsExpanded = useCallback((event, isExpanded) => {
		setExpandedSiteSection(
			isExpanded ? SITE_SECTION_SITE_INSPECTIONS : null
		);
	}, []);

	const onHazardsExpanded = useCallback((event, isExpanded) => {
		setExpandedSiteSection(isExpanded ? SITE_SECTION_HAZARDS : null);
	}, []);

	const onHazardousExpanded = useCallback((event, isExpanded) => {
		setExpandedSiteSection(isExpanded ? SITE_SECTION_HAZARDOUS : null);
	}, []);

	return (
		<>
			<ScrollToTopOnMount />
			<SitePinnedItems
				siteId={site.id}
				organisationId={organisationId}
				allowUnpin={allowUnpin}
			/>
			<div className={classes.siteInfoSlot}>
				<SiteInfoCard
					site={site}
					role={role}
					onEdit={onEdit}
					onDelete={onDelete}
					expanded={expandedSiteSection === SITE_SECTION_SITE_INFO}
					onExpandedChange={onSiteInfoExpanded}
				/>
			</div>
			<div className={classes.siteInfoSlot}>
				<SiteTimesheetsPanel
					site={site}
					expanded={expandedSiteSection === SITE_SECTION_TIMESHEETS}
					onExpandedChange={onTimesheetsExpanded}
				/>
			</div>
			<div className={classes.siteInfoSlot}>
				<SitePhotosNotesPanel
					site={site}
					organisationId={organisationId}
					expanded={expandedSiteSection === SITE_SECTION_PHOTOS_NOTES}
					onExpandedChange={onPhotosNotesExpanded}
				/>
			</div>
			<div className={classes.siteInfoSlot}>
				<SiteRecordSectionPanel
					site={site}
					organisationId={organisationId}
					expanded={expandedSiteSection === SITE_SECTION_INCIDENTS}
					onExpandedChange={onIncidentsExpanded}
					title="Incidents"
					countKey="incidentRecords"
					moduleTitle="Incidents"
					sectionId="incidents"
				>
					<IncidentSiteList variant="embedded" siteId={site.id} />
				</SiteRecordSectionPanel>
			</div>
			<div className={classes.siteInfoSlot}>
				<SiteRecordSectionPanel
					site={site}
					organisationId={organisationId}
					expanded={expandedSiteSection === SITE_SECTION_INDUCTIONS}
					onExpandedChange={onInductionsExpanded}
					title="Inductions"
					countKey="inductionMeetings"
					moduleTitle="Inductions"
					sectionId="inductions"
				>
					<InductionMinutesSiteList variant="embedded" siteId={site.id} />
				</SiteRecordSectionPanel>
			</div>
			<div className={classes.siteInfoSlot}>
				<SiteRecordSectionPanel
					site={site}
					organisationId={organisationId}
					expanded={expandedSiteSection === SITE_SECTION_SAFETY_MINUTES}
					onExpandedChange={onSafetyMinutesExpanded}
					title="Safety Minutes"
					countKey="safetyMinutes"
					moduleTitle="Safety Minutes"
					sectionId="safety-minutes"
				>
					<SafetyMinutesSiteList variant="embedded" siteId={site.id} />
				</SiteRecordSectionPanel>
			</div>
			<div className={classes.siteInfoSlot}>
				<SiteRecordSectionPanel
					site={site}
					organisationId={organisationId}
					expanded={expandedSiteSection === SITE_SECTION_SSPS}
					onExpandedChange={onSspsExpanded}
					title="Site Safety Plans (SSPs)"
					countKey="safetyPlanChecklists"
					moduleTitle="Site Safety Plans (SSPs)"
					sectionId="ssps"
				>
					<SafetyPlanChecklistSiteList variant="embedded" siteId={site.id} />
				</SiteRecordSectionPanel>
			</div>
			<div className={classes.siteInfoSlot}>
				<SiteRecordSectionPanel
					site={site}
					organisationId={organisationId}
					expanded={expandedSiteSection === SITE_SECTION_SITE_INSPECTIONS}
					onExpandedChange={onSiteInspectionsExpanded}
					title="Site Inspection Checklists"
					moduleTitle="Site Inspection Checklists"
					sectionId="site-inspections"
				>
					<SiteInspectionChecklistSiteList
						variant="embedded"
						siteId={site.id}
					/>
				</SiteRecordSectionPanel>
			</div>
			<div className={classes.siteInfoSlot}>
				<SiteRecordSectionPanel
					site={site}
					organisationId={organisationId}
					expanded={expandedSiteSection === SITE_SECTION_HAZARDS}
					onExpandedChange={onHazardsExpanded}
					title="Hazards"
					countKey="hazards"
					moduleTitle="Hazards"
					sectionId="hazards"
				>
					<HazardSiteList variant="embedded" siteId={site.id} />
				</SiteRecordSectionPanel>
			</div>
			<div className={classes.siteInfoSlot}>
				<SiteRecordSectionPanel
					site={site}
					organisationId={organisationId}
					expanded={expandedSiteSection === SITE_SECTION_HAZARDOUS}
					onExpandedChange={onHazardousExpanded}
					title="Hazardous Substances"
					countKey="hazardousSubstances"
					moduleTitle="Hazardous Substances"
					sectionId="hazardous-substances"
				>
					<HazardousSubstancesSiteList variant="embedded" siteId={site.id} />
				</SiteRecordSectionPanel>
			</div>
		</>
	);
};

Summary.propTypes = {
	site: PropTypes.object.isRequired,
	organisationId: PropTypes.string,
	role: PropTypes.string,
	onEdit: PropTypes.func,
	onDelete: PropTypes.func,
};

export default Summary;
